import { useState, useEffect } from "react";

interface CarouselSectionProps {
  title: string;
  description: string;
  popupTitle: string;
  popupParagraphs: string[];
  images: string[];
  rightBgClass?: string;
  rightBgStyle?: React.CSSProperties;
  imagePosition?: "left" | "right";
}

const CarouselSection = ({
  title,
  description,
  popupTitle,
  popupParagraphs,
  images,
  rightBgClass,
  rightBgStyle,
  imagePosition = "left",
}: CarouselSectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-change image every 3 seconds, paused if expanded
  useEffect(() => {
    if (isExpanded) return;

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [activeIndex, isExpanded, images.length]);

  return (
    <>
      <section className={`flex flex-col ${imagePosition === "right" ? "lg:flex-row-reverse" : "lg:flex-row"} w-full min-h-screen relative overflow-hidden lg:overflow-visible`}>
        {/* Left Side: Large Active Image */}
        <div className="relative w-full lg:w-[48%] h-[500px] lg:h-auto overflow-hidden">
          <img
            key={activeIndex}
            src={images[activeIndex]}
            alt={title}
            className="w-full h-full object-cover animate-fadeIn"
          />
        </div>

        {/* 5 Center Circles for Mobile Layout (horizontal row overlapping split line) */}
        <div className="flex lg:hidden justify-center gap-2.5 absolute w-full px-4 top-[472px] z-20">
          {images.map((img, idx) => (
            <div
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`w-14 h-14 rounded-full overflow-hidden border-2 cursor-pointer transition-all duration-300 bg-white ${
                activeIndex === idx
                  ? "border-red-500 scale-105 shadow-md"
                  : "border-white/50 opacity-40 brightness-75"
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Right Side: Text Description and Controls */}
        <div
          className={`relative w-full lg:w-[52%] text-white flex flex-col justify-start px-14 lg:px-28 pt-12 pb-28 ${
            rightBgClass || "bg-[#1c2c38]"
          }`}
          style={rightBgStyle}
        >
          <h2 className="text-4xl font-bold mb-4 mt-20">{title}</h2>
          <div className="text-gray-200 leading-relaxed mt-10 text-justify space-y-4">
            {isExpanded ? (
              popupParagraphs.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))
            ) : (
              <p>{description}</p>
            )}
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white underline hover:text-gray-300 transition w-fit mt-4 mb-10 cursor-pointer font-medium"
          >
            {isExpanded ? "Read Less" : "Read More"}
          </button>

          {/* 5 Center Circles for Desktop Layout (vertical stack on split boundary line) */}
          <div className={`hidden lg:flex flex-col gap-4 absolute top-1/2 -translate-y-1/2 ${imagePosition === "right" ? "right-[-60px]" : "left-[-60px]"} z-20`}>
            {images.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`w-24 h-24 rounded-full overflow-hidden border-4 cursor-pointer transition-all duration-300 bg-white ${
                  activeIndex === idx
                    ? "border-red-500 scale-105 shadow-xl"
                    : "border-white/80 opacity-40 hover:opacity-80 brightness-75 hover:scale-102"
                }`}
              >
                <img
                  src={img}
                  alt={`${title} thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Decorative Floating Circle */}
        {imagePosition !== "right" && (
          <div className="hidden lg:block absolute h-[130px] w-[130px] bottom-7 right-10 float-animation">
            <div
              className="w-28 h-28 rounded-full"
              style={{ backgroundColor: "rgba(232, 82, 34, 1)" }}
            />
          </div>
        )}
      </section>

    </>
  );
};

export default CarouselSection;
